import axios, { AxiosResponse } from 'axios';
import {
  Session,
  Message,
  MessageListResponse,
  SystemStats,
  CreateSessionRequest,
  CreateSessionResponse,
  SessionListResponse,
  Bot,
  SystemMessage,
  Language,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://telegram.talkappdev.com/';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds for session creation
  maxRedirects: 3,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Sessions API
export const sessionsApi = {
  getAll: (status?: string, page?: number, perPage?: number, search?: string): Promise<AxiosResponse<SessionListResponse>> =>
    api.get('api/v1/sessions', { 
      params: { 
        ...(status && { status }), 
        ...(page && { page }), 
        ...(perPage && { per_page: perPage }),
        ...(search && { search })
      } 
    }),
  
  getById: (sessionId: string): Promise<AxiosResponse<Session>> =>
    api.get(`api/v1/sessions/${sessionId}`),
  
  create: (data: CreateSessionRequest): Promise<AxiosResponse<CreateSessionResponse>> =>
    api.post('api/v1/sessions', data),
  
  update: (sessionId: string, data: Partial<Session>): Promise<AxiosResponse<Session>> =>
    api.patch(`api/v1/sessions/${sessionId}`, data),
  
  
  expireSessions: (): Promise<AxiosResponse<{ expired_sessions: number }>> =>
    api.post('api/v1/sessions/expire-sessions'),
  
  getUnused: (): Promise<AxiosResponse<Session>> =>
    api.get('api/v1/sessions/unused'),
  
  setAgentLanguage: (sessionId: string, languageId: number): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.post(`api/v1/sessions/${sessionId}/set-agent-language?language_id=${languageId}`),
};


// Messages API
export const messagesApi = {
  getAll: (params?: {
    session_id?: string;
    sender_type?: string;
    message_type?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }): Promise<AxiosResponse<MessageListResponse>> =>
    api.get('api/v1/messages', { params }),

  getBySession: (sessionId: string): Promise<AxiosResponse<Message[]>> =>
    api.get(`api/v1/messages/session/${sessionId}`),
  
  create: (data: Partial<Message>): Promise<AxiosResponse<Message>> =>
    api.post('api/v1/messages', data),
  
  update: (id: number, data: Partial<Message>): Promise<AxiosResponse<Message>> =>
    api.patch(`api/v1/messages/${id}`, data),
};

// System API
export const systemApi = {
  getStats: (): Promise<AxiosResponse<SystemStats>> =>
    api.get('api/v1/system/stats'),
  
  getHealth: (): Promise<AxiosResponse<{ status: string }>> =>
    api.get('api/v1/health'),
};

// Bots API
export const botsApi = {
  getAll: (): Promise<AxiosResponse<Bot[]>> =>
    api.get('api/v1/bots'),

  getById: (botId: number): Promise<AxiosResponse<Bot>> =>
    api.get(`api/v1/bots/${botId}`),

  create: (botToken: string, botUsername?: string, maxSessions?: number): Promise<AxiosResponse<Bot>> =>
    api.post('api/v1/bots', null, {
      params: {
        bot_token: botToken,
        ...(botUsername && { bot_username: botUsername }),
        ...(maxSessions && { max_sessions: maxSessions }),
      },
    }),

  updateAvailability: (botId: number, isAvailable: boolean): Promise<AxiosResponse<Bot>> =>
    api.put(`api/v1/bots/${botId}/availability`, null, {
      params: { is_available: isAvailable },
    }),

  updateActive: (botId: number, isActive: boolean): Promise<AxiosResponse<Bot>> =>
    api.put(`api/v1/bots/${botId}/active`, null, {
      params: { is_active: isActive },
    }),

  delete: (botId: number): Promise<AxiosResponse<{ detail: string }>> =>
    api.delete(`api/v1/bots/${botId}`),
};

// System Messages API
export const systemMessagesApi = {
  getAll: (category?: string, languageCode?: string): Promise<AxiosResponse<SystemMessage[]>> =>
    api.get('api/v1/system-messages', {
      params: {
        ...(category && { category }),
        ...(languageCode && { language_code: languageCode }),
      },
    }),

  getByKey: (key: string, languageCode?: string): Promise<AxiosResponse<SystemMessage>> =>
    api.get(`api/v1/system-messages/${key}`, {
      params: languageCode ? { language_code: languageCode } : {},
    }),

  update: (key: string, content: string): Promise<AxiosResponse<SystemMessage>> =>
    api.put(`api/v1/system-messages/${key}`, { content }),

  updateTranslation: (key: string, languageCode: string, content: string): Promise<AxiosResponse<SystemMessage>> =>
    api.put(`api/v1/system-messages/${key}/translations/${languageCode}`, { content }),

  deleteTranslation: (key: string, languageCode: string): Promise<AxiosResponse<SystemMessage>> =>
    api.delete(`api/v1/system-messages/${key}/translations/${languageCode}`),

  toggle: (key: string, is_active: boolean, languageCode?: string): Promise<AxiosResponse<SystemMessage>> =>
    api.patch(`api/v1/system-messages/${key}/toggle`, { is_active }, {
      params: languageCode ? { language_code: languageCode } : {},
    }),
};

export const languagesApi = {
  getTranslationLanguages: (): Promise<AxiosResponse<Language[]>> =>
    api.get('api/v1/languages/translation'),
};

// Error handler utility
export const handleApiError = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default api;
